import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LoaderCircle } from "lucide-react";
import { acceptInvite, getInviteInfo } from "../api/users";
import { useAuthStore } from "../store/authStore";
import { useLangPrefix } from "../hooks/useLangPrefix";
import { formatApiError } from "../lib/apiErrorMessage";
import LanguageSwitcher from "../components/layout/LanguageSwitcher";

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function isExpiredOrInvalidInvite(err) {
  const status = err?.response?.status;
  const code = String(err?.response?.data?.code || "").toLowerCase();
  const message = String(err?.response?.data?.message || "").toLowerCase();
  const signal = `${code} ${message}`;

  if ([400, 401, 403, 404, 410, 422].includes(status)) {
    return true;
  }

  return (
    signal.includes("invite") &&
    (signal.includes("expired") ||
      signal.includes("invalid") ||
      signal.includes("not found") ||
      signal.includes("token"))
  );
}

export default function AcceptInvitePage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { prefix } = useLangPrefix();
  const token = useAuthStore((s) => s.token);
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const userEmail = useAuthStore((s) => s.user?.email ?? s.user?.username ?? "");
  const logout = useAuthStore((s) => s.logout);

  const inviteFlow = searchParams.get("invite_flow")?.trim().toLowerCase() || "";
  const inviteToken = searchParams.get("invite_token")?.trim() || "";
  const callbackUrl = useMemo(
    () => `${location.pathname}${location.search}${location.hash}`,
    [location.pathname, location.search, location.hash]
  );
  const encodedCallbackUrl = useMemo(
    () => encodeURIComponent(callbackUrl),
    [callbackUrl]
  );

  const [view, setView] = useState("loading");
  const [inviteInfo, setInviteInfo] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const requestIdRef = useRef(0);
  const normalizedUserEmail = useMemo(() => normalizeEmail(userEmail), [userEmail]);

  useEffect(() => {
    if (!hasHydrated) return;
    if (!inviteToken) return;
    if (inviteFlow !== "signup") return;
    if (token) return;

    navigate(`${prefix}/signup?callback_url=${encodedCallbackUrl}`, { replace: true });
  }, [encodedCallbackUrl, hasHydrated, inviteFlow, inviteToken, navigate, prefix, token]);

  useEffect(() => {
    if (!hasHydrated) return;
    if (!inviteToken) return;
    if (inviteFlow === "signup") return;

    if (!token) {
      navigate(
        `${prefix}/login?callback_url=${encodedCallbackUrl}`,
        { replace: true }
      );
    }
  }, [encodedCallbackUrl, hasHydrated, inviteFlow, inviteToken, navigate, prefix, token]);

  useEffect(() => {
    if (!hasHydrated || !token || !inviteToken) return;

    const run = async () => {
      const requestId = ++requestIdRef.current;
      setView("loading");
      setErrorMessage("");

      let info;
      try {
        info = await getInviteInfo(inviteToken);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        if (isExpiredOrInvalidInvite(err)) {
          setView("expired");
        } else {
          setErrorMessage(formatApiError(err, "Request failed"));
          setView("error");
        }
        return;
      }

      if (requestId !== requestIdRef.current) return;
      setInviteInfo(info);

      if (normalizeEmail(info?.email) !== normalizedUserEmail) {
        setView("email_mismatch");
        return;
      }

      try {
        await acceptInvite(inviteToken);
        if (requestId !== requestIdRef.current) return;
        setView("success");
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        if (isExpiredOrInvalidInvite(err)) {
          setView("expired");
        } else {
          setErrorMessage(formatApiError(err, "Request failed"));
          setView("error");
        }
      }
    };

    void run();
  }, [hasHydrated, inviteFlow, inviteToken, normalizedUserEmail, token]);

  const handleLogout = async () => {
    await logout();
    if (inviteFlow === "signup" && inviteToken) {
      navigate(`${prefix}/signup?callback_url=${encodedCallbackUrl}`, { replace: true });
      return;
    }
    navigate(`${prefix}/login?callback_url=${encodedCallbackUrl}`, { replace: true });
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-zinc-100 p-4 text-zinc-900 scheme-light">
      <div className="absolute right-4 top-4 z-10 flex items-center gap-2">
        <LanguageSwitcher />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-indigo-100/70 via-transparent to-cyan-100/50" />
      <div className="w-full max-w-md relative">
        <div className="card p-6">
          <h2 className="mb-3 text-lg font-semibold text-zinc-900">
            {t("acceptInvite.title")}
          </h2>
          {inviteInfo?.email ? (
            <p className="mb-4 text-xs text-zinc-500">
              {t("acceptInvite.invitedEmail", { email: inviteInfo.email })}
            </p>
          ) : null}

          {view === "loading" && (
            <div className="flex items-center gap-2 text-sm text-zinc-600">
              <Spinner />
              {t("acceptInvite.loading")}
            </div>
          )}

          {view === "success" && (
            <div className="space-y-4">
              <p className="text-sm text-emerald-700">
                {t("acceptInvite.success")}
              </p>
              <button
                type="button"
                className="btn-primary w-full justify-center py-2.5"
                onClick={() => navigate(`${prefix}/dashboard`, { replace: true })}
              >
                {t("acceptInvite.backToDashboard")}
              </button>
            </div>
          )}

          {(!inviteToken || view === "invalid_token" || view === "expired") && (
            <p className="text-sm text-amber-700">
              {t("acceptInvite.expiredOrInvalid")}
            </p>
          )}

          {view === "email_mismatch" && (
            <div className="space-y-4">
              <p className="text-sm text-red-700">
                {t(
                  inviteFlow === "signup"
                    ? "acceptInvite.emailMismatchSignup"
                    : "acceptInvite.emailMismatch",
                  { email: inviteInfo?.email || "" }
                )}
              </p>
              <button
                type="button"
                className="btn-primary w-full justify-center py-2.5"
                onClick={handleLogout}
              >
                {t(
                  inviteFlow === "signup"
                    ? "acceptInvite.logoutAndSignup"
                    : "acceptInvite.logoutAndRelogin"
                )}
              </button>
            </div>
          )}

          {view === "error" && (
            <p className="text-sm text-red-700">
              {errorMessage || t("acceptInvite.genericError")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Spinner() {
  return <LoaderCircle className="h-4 w-4 animate-spin" />;
}
